// Calendar (U1) — legacy utilities/CalendarComponent.vue.
// `GET events?month=YYYY-MM` → `{ "YYYY-MM-DD": Event[] }`; add/edit/delete via
// `POST events`, `PUT events/{id}`, `DELETE events/{id}`.

import { useCallback, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import frLocale from '@fullcalendar/core/locales/fr';
import deLocale from '@fullcalendar/core/locales/de';
import type { DatesSetArg, EventClickArg, EventInput } from '@fullcalendar/core';
import type { DateClickArg } from '@fullcalendar/interaction';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { Add, Calendar } from 'iconsax-reactjs';

import { apiErrorMessage, apiValidationErrors } from '../../api/client';
import { can, isSuperUser } from '../../auth/permissions';
import { useAuth } from '../../components/AuthGate';
import { formatDate, getLocale, subscribeLocale, t } from '../../i18n';
import { CalendarEvent, CalendarEventInput, deleteEvent, fetchMonthEvents, saveEvent } from './api';
import { UI } from '../../brand/ui';

// Event colours from the brand palette only (no Quote green or POS blue). Events saved
// earlier keep their stored colour.
const COLORS = [UI.accent, UI.navy, UI.accentSoft, UI.slate, UI.muted, UI.warning, UI.danger];
const DEFAULT_COLOR = UI.accent;

const PLUGINS = [dayGridPlugin, interactionPlugin, listPlugin];
const LOCALES = [frLocale, deLocale];
const HEADER_TOOLBAR = { left: 'prev,next today', center: 'title', right: 'dayGridMonth,listMonth' };

const pad = (n: number) => String(n).padStart(2, '0');
const isoDate = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const monthKey = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
const hhmm = (time?: string | null) => (time ? time.slice(0, 5) : '');

function addDays(date: string, days: number) {
  const d = new Date(`${date}T00:00:00`);
  d.setDate(d.getDate() + days);
  return isoDate(d);
}

function toCalendarInput(event: CalendarEvent): EventInput {
  const start = event.start_time ? `${event.start_date}T${event.start_time}` : event.start_date;
  const endDate = event.end_date || event.start_date;
  const end = event.end_time ? `${endDate}T${event.end_time}` : event.end_date ? addDays(endDate, 1) : undefined;
  return {
    id: event.id,
    title: event.title,
    start,
    end,
    allDay: !event.start_time,
    backgroundColor: event.color || DEFAULT_COLOR,
    borderColor: event.color || DEFAULT_COLOR,
    extendedProps: { record: event }
  };
}

type FormState = CalendarEventInput & { id?: string };

const emptyForm = (date: string): FormState => ({
  title: '',
  start_date: date,
  end_date: date,
  start_time: '',
  end_time: '',
  color: DEFAULT_COLOR,
  details: ''
});

export function CalendarPage() {
  const { user } = useAuth();
  const locale = useSyncExternalStore(subscribeLocale, getLocale, getLocale);
  const calendarRef = useRef<FullCalendar | null>(null);
  const [events, setEvents] = useState<Record<string, CalendarEvent>>({});
  const [months, setMonths] = useState<string[]>([]);
  const [selectedDate, setSelectedDate] = useState(isoDate(new Date()));
  const [form, setForm] = useState<FormState | null>(null);
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [toast, setToast] = useState<{ msg: string; sev: 'success' | 'error' } | null>(null);

  const canCreate = isSuperUser(user) || can(user, 'create-events');
  const canUpdate = isSuperUser(user) || can(user, 'update-events');
  const canDelete = isSuperUser(user) || can(user, 'delete-events');

  const loadMonths = useCallback(async (keys: string[]) => {
    try {
      const results = await Promise.all(keys.map((key) => fetchMonthEvents(key)));
      setEvents((previous) => {
        const next: Record<string, CalendarEvent> = {};
        // keep events from months outside the refreshed set
        for (const [id, ev] of Object.entries(previous)) if (!keys.includes(ev.start_date.slice(0, 7))) next[id] = ev;
        for (const grouped of results) for (const list of Object.values(grouped)) for (const ev of list) next[ev.id] = ev;
        return next;
      });
    } catch (error) {
      setToast({ msg: apiErrorMessage(error, t('common.unableToLoad')), sev: 'error' });
    }
  }, []);

  const lastRange = useRef('');
  const onDatesSet = useCallback(
    (arg: DatesSetArg) => {
      // FullCalendar re-emits datesSet on re-render; only fetch when the range changes.
      const rangeKey = `${arg.startStr}|${arg.endStr}`;
      if (rangeKey === lastRange.current) return;
      lastRange.current = rangeKey;
      const keys: string[] = [];
      const cursor = new Date(arg.start.getFullYear(), arg.start.getMonth(), 1);
      const last = new Date(arg.end.getTime() - 1);
      while (cursor <= last) {
        keys.push(monthKey(cursor));
        cursor.setMonth(cursor.getMonth() + 1);
      }
      setMonths(keys);
      void loadMonths(keys);
    },
    [loadMonths]
  );

  const calendarEvents = useMemo(() => Object.values(events).map(toCalendarInput), [events]);
  const dayEvents = useMemo(
    () =>
      Object.values(events)
        .filter((ev) => ev.start_date <= selectedDate && (ev.end_date || ev.start_date) >= selectedDate)
        .sort((a, b) => (a.start_time ?? '').localeCompare(b.start_time ?? '')),
    [events, selectedDate]
  );

  const openCreate = (date: string) => {
    if (!canCreate) return;
    setErrors({});
    setForm(emptyForm(date));
  };

  const openEdit = (event: CalendarEvent) => {
    setErrors({});
    setForm({
      id: event.id,
      title: event.title,
      start_date: event.start_date,
      end_date: event.end_date ?? event.start_date,
      start_time: hhmm(event.start_time),
      end_time: hhmm(event.end_time),
      color: event.color || DEFAULT_COLOR,
      details: event.details ?? ''
    });
  };

  const onDateClick = (arg: DateClickArg) => {
    // First click selects the day (day list), a second click on it adds an event.
    const date = arg.dateStr.slice(0, 10);
    if (date === selectedDate) openCreate(date);
    setSelectedDate(date);
  };

  const onEventClick = (arg: EventClickArg) => {
    const record = arg.event.extendedProps.record as CalendarEvent;
    setSelectedDate(record.start_date);
    openEdit(record);
  };

  const submit = async () => {
    if (!form) return;
    setSaving(true);
    setErrors({});
    const { id, ...payload } = form;
    const body: CalendarEventInput = {
      ...payload,
      end_date: payload.end_date || null,
      start_time: payload.start_time || null,
      end_time: payload.end_time || null,
      details: payload.details || null
    };
    try {
      await saveEvent(body, id);
      setForm(null);
      setToast({ msg: t(id ? 'shell.calendar.updated' : 'shell.calendar.created'), sev: 'success' });
      await loadMonths(months.length ? months : [body.start_date.slice(0, 7)]);
    } catch (error) {
      setErrors(apiValidationErrors(error));
      setToast({ msg: apiErrorMessage(error, t('common.saveFailed')), sev: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!form?.id) return;
    setSaving(true);
    try {
      await deleteEvent(form.id);
      setEvents((previous) => {
        const next = { ...previous };
        delete next[form.id!];
        return next;
      });
      setForm(null);
      setConfirmDelete(false);
      setToast({ msg: t('common.deleted'), sev: 'success' });
    } catch (error) {
      setToast({ msg: apiErrorMessage(error, t('common.deleteFailed')), sev: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const fieldError = (key: string) => errors[key]?.[0];
  const setField = (key: keyof FormState, value: string) => setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
  const readOnly = Boolean(form?.id) && !canUpdate;

  return (
    <Stack spacing={2.5}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Calendar size={28} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {t('nav.calendar')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('shell.calendar.subtitle')}
            </Typography>
          </Box>
        </Stack>
        {canCreate ? (
          <Button variant="contained" startIcon={<Add size={18} />} onClick={() => openCreate(selectedDate)}>
            {t('shell.calendar.add')}
          </Button>
        ) : null}
      </Stack>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 300px' }, gap: 2.5, alignItems: 'start' }}>
        <Card>
          <CardContent
            data-testid="calendar"
            sx={{
              '& .fc': { fontFamily: 'inherit' },
              '& .fc .fc-toolbar-title': { fontSize: '1.15rem', fontWeight: 700 },
              '& .fc .fc-button-primary': { bgcolor: 'primary.main', borderColor: 'primary.main', textTransform: 'capitalize' },
              [`& .fc-day[data-date="${selectedDate}"]`]: { bgcolor: UI.accentAlpha(0.07) }
            }}
          >
            <FullCalendar
              ref={calendarRef}
              plugins={PLUGINS}
              initialView="dayGridMonth"
              headerToolbar={HEADER_TOOLBAR}
              locales={LOCALES}
              locale={locale}
              height="auto"
              dayMaxEvents={3}
              events={calendarEvents}
              datesSet={onDatesSet}
              dateClick={onDateClick}
              eventClick={onEventClick}
            />
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {formatDate(selectedDate)}
              </Typography>
              {canCreate ? (
                <Tooltip title={t('shell.calendar.add')}>
                  <IconButton size="small" onClick={() => openCreate(selectedDate)} aria-label={t('shell.calendar.add')}>
                    <Add size={18} />
                  </IconButton>
                </Tooltip>
              ) : null}
            </Stack>
            {dayEvents.length ? (
              <List dense disablePadding data-testid="day-events">
                {dayEvents.map((event) => (
                  <ListItemButton key={event.id} onClick={() => openEdit(event)} sx={{ borderRadius: 1, borderLeft: `4px solid ${event.color || DEFAULT_COLOR}`, mb: 0.5 }}>
                    <ListItemText
                      primary={event.title}
                      secondary={[event.start_time ? `${hhmm(event.start_time)}${event.end_time ? ` – ${hhmm(event.end_time)}` : ''}` : t('shell.calendar.allDay'), event.details]
                        .filter(Boolean)
                        .join(' · ')}
                    />
                  </ListItemButton>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t('shell.calendar.noEvents')}
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>

      <Dialog open={Boolean(form)} onClose={() => !saving && setForm(null)} fullWidth maxWidth="sm">
        <DialogTitle>{form?.id ? t('shell.calendar.edit') : t('shell.calendar.add')}</DialogTitle>
        <DialogContent dividers>
          {form ? (
            <Stack spacing={2}>
              <TextField
                autoFocus
                required
                label={t('shell.calendar.title')}
                value={form.title}
                onChange={(e) => setField('title', e.target.value)}
                error={Boolean(fieldError('title'))}
                helperText={fieldError('title')}
                inputProps={{ maxLength: 90 }}
                disabled={readOnly}
              />
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <TextField
                  required
                  type="date"
                  label={t('shell.calendar.startDate')}
                  value={form.start_date}
                  onChange={(e) => setField('start_date', e.target.value)}
                  error={Boolean(fieldError('start_date'))}
                  helperText={fieldError('start_date')}
                  InputLabelProps={{ shrink: true }}
                  disabled={readOnly}
                />
                <TextField
                  type="date"
                  label={t('shell.calendar.endDate')}
                  value={form.end_date ?? ''}
                  onChange={(e) => setField('end_date', e.target.value)}
                  error={Boolean(fieldError('end_date'))}
                  helperText={fieldError('end_date')}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: form.start_date }}
                  disabled={readOnly}
                />
                <TextField
                  type="time"
                  label={t('shell.calendar.startTime')}
                  value={form.start_time ?? ''}
                  onChange={(e) => setField('start_time', e.target.value)}
                  error={Boolean(fieldError('start_time'))}
                  helperText={fieldError('start_time')}
                  InputLabelProps={{ shrink: true }}
                  disabled={readOnly}
                />
                <TextField
                  type="time"
                  label={t('shell.calendar.endTime')}
                  value={form.end_time ?? ''}
                  onChange={(e) => setField('end_time', e.target.value)}
                  error={Boolean(fieldError('end_time'))}
                  helperText={fieldError('end_time')}
                  InputLabelProps={{ shrink: true }}
                  disabled={readOnly}
                />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                  {t('shell.calendar.color')}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5, flexWrap: 'wrap', rowGap: 1 }}>
                  {COLORS.map((color) => (
                    <Box
                      key={color}
                      component="button"
                      type="button"
                      aria-label={color}
                      disabled={readOnly}
                      onClick={() => setField('color', color)}
                      sx={{
                        width: 26,
                        height: 26,
                        borderRadius: '50%',
                        bgcolor: color,
                        cursor: 'pointer',
                        border: form.color === color ? `3px solid ${UI.ink}` : '2px solid rgba(255,255,255,0.9)',
                        boxShadow: UI.shadowSm
                      }}
                    />
                  ))}
                  <input
                    type="color"
                    aria-label={t('shell.calendar.customColor')}
                    value={form.color || DEFAULT_COLOR}
                    disabled={readOnly}
                    onChange={(e) => setField('color', e.target.value)}
                    style={{ width: 34, height: 28, border: 'none', background: 'none', cursor: 'pointer' }}
                  />
                </Stack>
              </Box>
              <TextField
                multiline
                minRows={3}
                label={t('shell.calendar.details')}
                value={form.details ?? ''}
                onChange={(e) => setField('details', e.target.value)}
                error={Boolean(fieldError('details'))}
                helperText={fieldError('details')}
                inputProps={{ maxLength: 190 }}
                disabled={readOnly}
              />
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3 }}>
          <Box>
            {form?.id && canDelete ? (
              <Button color="error" disabled={saving} onClick={() => setConfirmDelete(true)}>
                {t('action.delete')}
              </Button>
            ) : null}
          </Box>
          <Stack direction="row" spacing={1}>
            <Button onClick={() => setForm(null)} disabled={saving}>
              {t('action.cancel')}
            </Button>
            {!readOnly ? (
              <Button variant="contained" onClick={() => void submit()} disabled={saving || !form?.title || !form?.start_date}>
                {saving ? t('action.saving') : t('action.save')}
              </Button>
            ) : null}
          </Stack>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <DialogTitle>{t('common.confirmDelete')}</DialogTitle>
        <DialogContent>
          <Typography>{form?.title}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)}>{t('action.cancel')}</Button>
          <Button color="error" variant="contained" disabled={saving} onClick={() => void remove()}>
            {t('action.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        {toast ? (
          <Alert severity={toast.sev} onClose={() => setToast(null)}>
            {toast.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Stack>
  );
}
